import TextField, { BaseTextFieldProps } from "@mui/material/TextField";
import { TextFieldVariants } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useState, useEffect, useRef, useMemo } from "react";
import Title, { InvalidTitleError } from "_shared/titles/title";
import { debounce } from "lodash";

const InputField = styled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(1.5),
}));

interface TitleInputProps extends BaseTextFieldProps {
  idPrefix?: string;
  className?: string;
  variant?: TextFieldVariants;
  disabled?: boolean;
  initialValue?: Title;
  label?: string;
  /**
   * This method is called when the name instance is modified. This callback is only
   * registered initially, to reregister the handler, change the key
   * @returns ({@link Title}, undefined) when successful
   * @returns (undefined, errorMessage: string) when unsuccessful
   */
  onChange?: (title?: Title, errorMessage?: string) => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLDivElement>) => void;
};

export default function TitleInput(props: Readonly<TitleInputProps>) {
  const {
    idPrefix,
    className,
    variant,
    disabled,
    initialValue,
    label,
    onChange,
    onKeyDown,
    ...otherProps
  } = props;

  const [titleText, setTitleText] = useState<string>(initialValue?.value ?? '');
  const [titleError, setTitleError] = useState<string>('');

 const titleIsDirty = useRef<boolean>(false);

  const debouncedValidate = useMemo(() => 
    debounce((titleString: string, isDirty: boolean) => {
      let title: Title | undefined;
      let newErrors: string = '';
      
      if(!isDirty) return;
      try {
        title = new Title(titleString);
        setTitleError('');
      } catch (error) {
        if (error instanceof InvalidTitleError) {
          newErrors = error.message;
          setTitleError(newErrors);
        } else {
          throw error;
        }
      } finally {
        onChange?.(title, newErrors);
      }
    }, 300),
  // We cannot guarantee onChange being stable, if it changes, change the key to recreate the component
  // eslint-disable-next-line react-hooks/exhaustive-deps
  []);

  useEffect(() => {
    debouncedValidate(titleText, titleIsDirty.current);
    return () => debouncedValidate.cancel();
  }, [titleText, debouncedValidate]);

  return (
    <InputField
      id={idPrefix ? idPrefix + "-title-input" : "title-input"}
      className={className}
      label={label ?? "Title"}
      onChange={(event) => {
        setTitleText(prev => {
          if(titleIsDirty.current) return event.target.value
          if(prev !== event.target.value)
          {
            titleIsDirty.current = true;
            return event.target.value;
          }
          return prev;
        });
      }}
      required
      disabled={disabled}
      value={titleText}
      error={!!titleError}
      helperText={titleError}
      variant={variant ?? "outlined"}
      autoComplete="organization"
      onKeyDown={onKeyDown}
      {...otherProps}
    />
  );
}